import { useState } from "react";
import { toast } from "sonner";
import { Edit, Trash2, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useFetchSaccoShares,
  useDeleteSaccoShare,
} from "@saccoHooks";
import { ShareForm } from "./ShareForm";
import {SaccoShare} from "@/@types/sacco.models.ts";

interface ShareListProps {
  membershipId: string;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(dateStr: string) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ShareList({
  membershipId,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: ShareListProps) {
  const { data: shares = [], isLoading } = useFetchSaccoShares(membershipId);
  const deleteShare = useDeleteSaccoShare(membershipId);

  const [formOpen, setFormOpen] = useState(false);
  const [editingShare, setEditingShare] = useState<SaccoShare | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sorted = [...shares].sort(
    (a, b) => new Date(a.purchase_date).getTime() - new Date(b.purchase_date).getTime()
  );

  function handleEdit(share: SaccoShare) {
    setEditingShare(share);
    setFormOpen(true);
  }

  function handleAddNew() {
    setEditingShare(undefined);
    setFormOpen(true);
  }

  function handleDeleteClick(id: string) {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deletingId) return;
    try {
      await deleteShare.mutateAsync(deletingId);
      toast.success("Share record deleted successfully");
    } catch {
      toast.error("Failed to delete share record");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleAddNew} size="sm">
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Share Purchase
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">Loading shares...</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No share purchases recorded yet. Add your first share purchase above.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Units</TableHead>
              <TableHead className="text-right">Price/Unit</TableHead>
              <TableHead className="text-right">Total Value</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((share) => {
              const totalValue = Number(share.units) * Number(share.price_per_unit);
              const effectiveCurrency = ratesError ? nativeCurrency : displayCurrency;

              function convertAmt(raw: number): number {
                if (ratesError || ratesLoading || !convert || displayCurrency === nativeCurrency) return raw;
                try {
                  return convert(raw, nativeCurrency, displayCurrency);
                } catch {
                  return raw;
                }
              }

              return (
                <TableRow key={share.id}>
                  <TableCell>{formatDate(share.purchase_date)}</TableCell>
                  <TableCell className="text-right">{Number(share.units).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmt(Number(share.price_per_unit)), effectiveCurrency)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmt(totalValue), effectiveCurrency)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {share.note ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(share)}
                        aria-label="Edit share"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteClick(share.id)}
                        disabled={deleteShare.isPending}
                        aria-label="Delete share"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ShareForm
        membershipId={membershipId}
        nativeCurrency={nativeCurrency}
        share={editingShare}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingShare(undefined);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Share Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this share purchase record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
